import { useOnboardingStore } from '../store/onboardingStore';
import { useStoryStore } from '../store/storyStore';
import { useFamilyGroupStore } from '../store/familyGroupStore';
import { useFamilyInvitationStore } from '../store/familyInvitationStore';
import {
  isTutorialActive,
  isAudienceInviteEligible,
} from '../lib/promptPredicates';

export type PromptId = 'audienceInvite' | null;

// Returns the single highest-priority dashboard prompt that should render
// right now, or null. Priority order:
//   1. tutorial active        → suppress everything else
//   2. pending invitations    → exclusive attention
//   3. audienceInvite         → engagement loop, post first-chapter milestone
//
// PRO-348 dropped the `pwaInstall` branch — install now lives at story
// creation, AcceptInvitationPage, the activate-push CTA inside reminder
// emails, and the standalone PermissionRecoveryBanner /
// NotificationBackfillBanner that Dashboard renders separately.
export const usePromptOrchestrator = (): PromptId => {
  const { loaded, stepsCompleted } = useOnboardingStore();
  const stories = useStoryStore(s => s.stories);
  const getActiveFamily = useFamilyGroupStore(s => s.getActiveFamily);
  const activeFamily = getActiveFamily();
  const receivedInvitationsCount = useFamilyInvitationStore(s => s.receivedCount);

  if (isTutorialActive({ loaded, stepsCompleted })) return null;

  // Pending family invitations take exclusive attention. The
  // PendingInvitationsBanner publishes its count after fetch via
  // setReceivedCount; if there is at least one waiting, suppress every
  // orchestrated prompt this render so the user is not asked to do two
  // things at once. -1 = not yet loaded → don't suppress.
  if (receivedInvitationsCount > 0) return null;

  if (isAudienceInviteEligible(activeFamily, stories)) return 'audienceInvite';

  return null;
};
